import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ArrowLeft, FileText, User, CalendarDays, Save, Sparkles, Loader2, UploadCloud, X } from "lucide-react";
import { toast } from "sonner";
import api from "../../api/axios";
import { API_URL } from "../../api/api";
import {
  LETTER_TYPES, DEFAULT_TEMPLATES, replacePlaceholders,
} from "../../utils/letterTemplates";
import { T, Card, PrimaryButton, SecondaryButton, Select, Label, Input } from "../../components/em/ui";

/* Placeholder fields per letter type (mirrors the Settings generator). Only
   the fields whose {{key}} appears in the chosen template are shown. */
const PLACEHOLDER_FIELDS = [
  { key: "employeeName", label: "Employee Name", type: "text" },
  { key: "designation", label: "Designation", type: "text" },
  { key: "newDesignation", label: "New Designation", type: "text" },
  { key: "department", label: "Department", type: "text" },
  { key: "companyName", label: "Company Name", type: "text" },
  { key: "manager", label: "Reporting Manager", type: "text" },
  { key: "salary", label: "Salary (CTC)", type: "text" },
  { key: "newSalary", label: "New Salary", type: "text" },
  { key: "currentSalary", label: "Current Salary", type: "text" },
  { key: "incrementPercentage", label: "Increment %", type: "number" },
  { key: "joinDate", label: "Join Date", type: "date" },
  { key: "effectiveDate", label: "Effective Date", type: "date" },
  { key: "lastWorkingDay", label: "Last Working Day", type: "date" },
  { key: "responseDate", label: "Response Deadline", type: "date" },
  { key: "confirmationDate", label: "Confirmation Date", type: "date" },
  { key: "issueDate", label: "Issue Date", type: "date" },
  { key: "offerDate", label: "Offer Date", type: "date" },
  { key: "incidentDate", label: "Incident Date", type: "date" },
  { key: "startDate", label: "Start Date", type: "date" },
  { key: "probationPeriod", label: "Probation Period (months)", type: "number" },
  { key: "duration", label: "Duration", type: "text" },
  { key: "stipend", label: "Stipend", type: "text" },
  { key: "responsibilities", label: "Responsibilities", type: "textarea" },
  { key: "qualities", label: "Qualities / Skills", type: "text" },
  { key: "project", label: "Project / Task", type: "text" },
  { key: "description", label: "Description", type: "textarea" },
  { key: "issue", label: "Issue / Reason", type: "text" },
  { key: "location", label: "Location", type: "text" },
  { key: "signatoryName", label: "Signatory Name", type: "text" },
  { key: "signatoryTitle", label: "Signatory Title", type: "text" },
  { key: "employeeEmail", label: "Employee Email", type: "email" },
];

const CreateNew = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const role = (localStorage.getItem("role") || "").toLowerCase().trim();
  const basePath = role === "hr" ? "/hr" : "/admin";
  const userData = useMemo(
    () => JSON.parse(localStorage.getItem("userdata") || "{}"),
    [],
  );
  const companyNameDefault = useRef("");

  // When arriving from "Edit" on the letters list, the route carries a prefill
  // (type + already-resolved field values) so the user can tweak & regenerate.
  const prefill = location.state?.prefill || null;
  // Applied once, after the template for the prefilled type finishes loading —
  // the load effect resets `form` to {}, so we stash the values and restore them.
  const prefillValues = useRef(prefill?.preview_values || null);

  const [selectedType, setSelectedType] = useState(prefill?.document_type || "offer");
  const [subjectTemplate, setSubjectTemplate] = useState("");
  const [bodyTemplate, setBodyTemplate] = useState("");
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Signature: defaults to the company's authorized signature (from
  // /settings/company-info). Uploading here overrides it for this letter only.
  const [companySignatureUrl, setCompanySignatureUrl] = useState("");
  const [signatureDataUrl, setSignatureDataUrl] = useState(null); // base64 override; null = use company default
  const [dragOver, setDragOver] = useState(false);
  const sigInputRef = useRef(null);
  const signaturePreview = signatureDataUrl || companySignatureUrl;

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoadingTemplate(true);
      try {
        // On first load, fetch company info in parallel with the templates so
        // companyName is guaranteed to be available when the form is seeded.
        const fetches = [api.get("/settings/letter-templates")];
        if (!companyNameDefault.current) fetches.push(api.get("/settings/company-info"));
        const [templateRes, companyRes] = await Promise.allSettled(fetches);

        if (companyRes?.status === "fulfilled" && companyRes.value?.data) {
          const cd = companyRes.value.data;
          if (cd.company_name) companyNameDefault.current = cd.company_name;
          if (alive && cd.authorized_signature) {
            setCompanySignatureUrl(`${API_URL}${cd.authorized_signature}`);
          }
        }

        const templateData = templateRes.status === "fulfilled" ? templateRes.value.data : null;
        const match = Array.isArray(templateData)
          ? templateData.find((t) => t.document_type === selectedType)
          : null;
        const def = DEFAULT_TEMPLATES[selectedType];
        if (!alive) return;
        setSubjectTemplate(match?.subject || def?.subject || "");
        setBodyTemplate(match?.body || def?.content || "");
      } catch {
        const def = DEFAULT_TEMPLATES[selectedType];
        if (!alive) return;
        setSubjectTemplate(def?.subject || "");
        setBodyTemplate(def?.content || "");
      } finally {
        if (alive) {
          setLoadingTemplate(false);
          const defaults = companyNameDefault.current ? { companyName: companyNameDefault.current } : {};
          setForm({ ...defaults, ...(prefillValues.current || {}) });
          prefillValues.current = null;
        }
      }
    })();
    return () => { alive = false; };
  }, [selectedType]);

  const relevantFields = useMemo(() => {
    const combined = `${subjectTemplate}${bodyTemplate}`;
    return PLACEHOLDER_FIELDS.filter((f) => combined.includes(`{{${f.key}}}`));
  }, [subjectTemplate, bodyTemplate]);

  const handleChange = (e) =>
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  // ── Signature upload (drag-drop / click) ──
  const acceptSignatureFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file (PNG, JPG…)");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Signature image must be under 2 MB");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setSignatureDataUrl(reader.result);
    reader.onerror = () => toast.error("Could not read that image");
    reader.readAsDataURL(file);
  };
  const onSigDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    acceptSignatureFile(e.dataTransfer.files?.[0]);
  };
  const resetSignature = () => {
    setSignatureDataUrl(null);
    if (sigInputRef.current) sigInputRef.current.value = "";
  };

  const renderedSubject = replacePlaceholders(subjectTemplate, form);
  const renderedBody = replacePlaceholders(bodyTemplate, form);
  const typeLabel = LETTER_TYPES.find((t) => t.value === selectedType)?.label || selectedType;

  // Split the rendered body so the signature image can sit just above the
  // signatory name (mirrors how the backend places it in the PDF).
  const previewSegments = useMemo(() => {
    if (!signaturePreview) return null;
    const text = renderedBody;
    // Anchor on the closing salutation first (the signature follows it); fall
    // back to the LAST mention of the signatory name, else append at the end.
    let idx = -1;
    const m = text.match(/\n[ \t]*(Sincerely|Best regards|Warm regards|Kind regards|Regards|Yours sincerely|Yours faithfully)[,]?[ \t]*\n/i);
    if (m) idx = m.index + m[0].length;
    if (idx === -1) {
      const name = (form.signatoryName || form.signatory_name || "").trim();
      if (name) idx = text.lastIndexOf(name);
    }
    if (idx === -1) return { before: text, after: "" };
    return { before: text.slice(0, idx), after: text.slice(idx) };
  }, [renderedBody, signaturePreview, form.signatoryName, form.signatory_name]);

  const handleSave = async (isFinal) => {
    setSaving(true);
    try {
      await api.post("/settings/letter-templates/generate-pdf", {
        template_name: typeLabel,
        document_type: selectedType,
        subject: subjectTemplate,
        body: bodyTemplate,
        placeholders: relevantFields.map((f) => f.key),
        preview_values: form,
        created_by: userData.id,
        is_final: isFinal,
        // Per-letter signature override; null tells the backend to use the
        // company's authorized signature.
        signature_data_url: signatureDataUrl,
      });
      toast.success(isFinal ? "Letter generated & saved!" : "Saved as draft");
      navigate(`${basePath}/generateletter`);
    } catch (err) {
      console.error("Error saving letter:", err);
      toast.error("Failed to save letter");
    } finally {
      setSaving(false);
    }
  };

  const labelStyle = { font: `600 11px ${T.font}`, color: T.fg5, letterSpacing: "0.03em", textTransform: "uppercase" };

  return (
    <div style={{ padding: "22px 28px 48px", maxWidth: 1440, margin: "0 auto" }}>
      {/* Back + title */}
      <button
        type="button"
        onClick={() => navigate(`${basePath}/generateletter`)}
        style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: 0, cursor: "pointer", color: T.fg5, font: `500 13px ${T.font}`, padding: 0, marginBottom: 14 }}
      >
        <ArrowLeft size={15} /> Back to letters
      </button>

      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
        <div style={{ width: 46, height: 46, borderRadius: 12, background: T.orange50, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <FileText size={22} color={T.orange} />
        </div>
        <div>
          <div style={{ font: `700 22px ${T.font}`, color: T.fg1, letterSpacing: "-0.02em" }}>{prefill ? "Edit & Regenerate Letter" : "Generate New Letter"}</div>
          <div style={{ font: `400 13px ${T.font}`, color: T.fg5 }}>{prefill ? "Adjust the details below and generate an updated letter." : "Pick a type, fill the details, preview live, then generate."}</div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 18, alignItems: "flex-start", flexWrap: "wrap" }}>
        {/* ── LEFT: summary + actions ── */}
        <div style={{ width: 250, flexShrink: 0, display: "flex", flexDirection: "column", gap: 14, minWidth: 220 }}>
          <Card style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ font: `700 12px ${T.font}`, color: T.fg1, textTransform: "uppercase", letterSpacing: "0.04em" }}>Summary</div>
            <SummaryRow icon={User} label="Recipient" value={form.employeeName || "—"} />
            <SummaryRow icon={FileText} label="Document" value={typeLabel} />
            <SummaryRow icon={CalendarDays} label="Fields filled" value={`${Object.values(form).filter(Boolean).length}/${relevantFields.length}`} />
          </Card>

          <Card style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <PrimaryButton
              icon={saving ? Loader2 : Sparkles}
              onClick={() => handleSave(true)}
              disabled={saving}
              style={{ justifyContent: "center", ...(saving ? { opacity: 0.7 } : {}) }}
            >
              {saving ? "Working…" : "Generate & Save"}
            </PrimaryButton>
            <SecondaryButton
              icon={Save}
              onClick={() => handleSave(false)}
              disabled={saving}
              style={{ justifyContent: "center" }}
            >
              Save as draft
            </SecondaryButton>
          </Card>
        </div>

        {/* ── CENTER: live preview ── */}
        <div style={{ flex: 1, minWidth: 320 }}>
          <Card padding={0} style={{ overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 18px", borderBottom: `1px solid ${T.divider}`, background: T.bgHead }}>
              <span style={{ font: `700 13px ${T.font}`, color: T.fg1 }}>Live Preview</span>
              <span style={{ font: `600 11px ${T.font}`, color: T.orange, background: T.orange50, padding: "3px 10px", borderRadius: 9999 }}>{typeLabel}</span>
            </div>
            <div style={{ padding: 28, background: T.bgHead, minHeight: 420, display: "flex", justifyContent: "center" }}>
              {/* paper */}
              <div style={{ width: "100%", maxWidth: 640, background: "#fff", borderRadius: 8, boxShadow: T.shadowMd, padding: "44px 48px", minHeight: 380 }}>
                {loadingTemplate ? (
                  <div style={{ color: "#6a7282", font: `400 13px ${T.font}` }}>Loading template…</div>
                ) : (
                  <>
                    <div style={{ font: `700 15px/1.5 ${T.font}`, color: "#0a0a0a", marginBottom: 18 }}>
                      {renderedSubject || <span style={{ color: "#9ca3af", fontWeight: 400 }}>No subject</span>}
                    </div>
                    <div style={{ font: `400 13px/1.8 ${T.font}`, color: "#1f2937", whiteSpace: "pre-wrap" }}>
                      {!renderedBody ? (
                        <span style={{ color: "#9ca3af" }}>No content yet.</span>
                      ) : previewSegments ? (
                        <>
                          {previewSegments.before}
                          <img
                            src={signaturePreview}
                            alt="Signature"
                            style={{ maxHeight: 60, display: "block", margin: "8px 0 2px" }}
                          />
                          {previewSegments.after}
                        </>
                      ) : (
                        renderedBody
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </Card>
        </div>

        {/* ── RIGHT: form ── */}
        <div style={{ width: 360, flexShrink: 0, minWidth: 300 }}>
          <Card style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div>
              <Label>Document Type</Label>
              <Select value={selectedType} onChange={(e) => setSelectedType(e.target.value)}>
                {LETTER_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </Select>
            </div>

            {relevantFields.length > 0 && (
              <div style={{ borderTop: `1px solid ${T.divider}`, paddingTop: 12, display: "flex", flexDirection: "column", gap: 12 }}>
                <div style={labelStyle}>Letter details</div>
                {relevantFields.map((f) => (
                  <div key={f.key}>
                    <Label>{f.label}</Label>
                    {f.type === "textarea" ? (
                      <textarea
                        name={f.key}
                        value={form[f.key] || ""}
                        onChange={handleChange}
                        rows={3}
                        placeholder={`Enter ${f.label.toLowerCase()}…`}
                        style={{ width: "100%", padding: "9px 12px", border: `1px solid ${T.border}`, borderRadius: T.rMd, background: "var(--em-bg-card)", font: `400 13px/1.5 ${T.font}`, color: T.fg1, outline: "none", boxSizing: "border-box", resize: "vertical" }}
                      />
                    ) : (
                      <Input
                        type={f.type}
                        name={f.key}
                        value={form[f.key] || ""}
                        onChange={handleChange}
                        placeholder={`Enter ${f.label.toLowerCase()}…`}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* ── Signature ── */}
            <div style={{ borderTop: `1px solid ${T.divider}`, paddingTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <span style={labelStyle}>Signature</span>
                <span style={{ font: `500 10.5px ${T.font}`, color: signatureDataUrl ? T.orange : T.fg7 }}>
                  {signatureDataUrl ? "Custom for this letter" : "Company default"}
                </span>
              </div>

              <div
                onClick={() => sigInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onSigDrop}
                style={{
                  border: `1.5px dashed ${dragOver ? T.orange : T.border}`,
                  borderRadius: T.rMd,
                  background: dragOver ? T.orange50 : T.bgHead,
                  padding: signaturePreview ? "12px" : "20px 12px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 8,
                  cursor: "pointer",
                  textAlign: "center",
                  transition: "border-color .15s, background .15s",
                }}
              >
                {signaturePreview ? (
                  <img
                    src={signaturePreview}
                    alt="Signature"
                    style={{ maxHeight: 64, maxWidth: "100%", objectFit: "contain" }}
                  />
                ) : (
                  <div style={{ width: 34, height: 34, borderRadius: 9, background: T.orange50, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <UploadCloud size={17} color={T.orange} />
                  </div>
                )}
                <div style={{ font: `500 12px ${T.font}`, color: T.fg5 }}>
                  {signaturePreview ? "Click or drop to replace" : "Drag & drop or click to upload"}
                </div>
                <div style={{ font: `400 10.5px ${T.font}`, color: T.fg7 }}>PNG or JPG, up to 2 MB</div>
              </div>

              {signatureDataUrl && (
                <button
                  type="button"
                  onClick={resetSignature}
                  style={{ alignSelf: "flex-start", display: "inline-flex", alignItems: "center", gap: 5, background: "none", border: 0, cursor: "pointer", color: T.fg5, font: `500 12px ${T.font}`, padding: 0 }}
                >
                  <X size={13} /> Reset to company default
                </button>
              )}

              <input
                ref={sigInputRef}
                type="file"
                accept="image/*"
                onChange={(e) => acceptSignatureFile(e.target.files?.[0])}
                style={{ display: "none" }}
              />
            </div>

            <div style={{ borderTop: `1px solid ${T.divider}`, paddingTop: 12, display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={labelStyle}>Template</div>
              <div>
                <Label>Subject</Label>
                <Input value={subjectTemplate} onChange={(e) => setSubjectTemplate(e.target.value)} placeholder="Subject line…" />
              </div>
              <div>
                <Label hint="use {{placeholders}}">Body</Label>
                <textarea
                  value={bodyTemplate}
                  onChange={(e) => setBodyTemplate(e.target.value)}
                  rows={12}
                  placeholder="Letter body…"
                  style={{ width: "100%", padding: "10px 12px", border: `1px solid ${T.border}`, borderRadius: T.rMd, background: "var(--em-bg-card)", font: `400 13px/1.7 ${T.font}`, color: T.fg1, outline: "none", boxSizing: "border-box", resize: "vertical" }}
                />
              </div>
            </div>

            <PrimaryButton
              icon={saving ? Loader2 : Sparkles}
              onClick={() => handleSave(true)}
              disabled={saving}
              style={{ justifyContent: "center", ...(saving ? { opacity: 0.7 } : {}) }}
            >
              {saving ? "Working…" : "Generate and Save Letter"}
            </PrimaryButton>
          </Card>
        </div>
      </div>
    </div>
  );
};

const SummaryRow = ({ icon: Icon, label, value }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
    <div style={{ width: 30, height: 30, borderRadius: 8, background: T.bgHead, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      <Icon size={15} color={T.fg5} />
    </div>
    <div style={{ minWidth: 0 }}>
      <div style={{ font: `400 10.5px ${T.font}`, color: T.fg7, textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</div>
      <div style={{ font: `600 13px ${T.font}`, color: T.fg1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{value}</div>
    </div>
  </div>
);

export default CreateNew;
